<script setup>
import { ref, watch, onMounted, nextTick, computed } from "vue";
import { useRouter } from "vue-router";
import SearchJobApi from "@api/searchJob.api";
import Storage from "@services/storage";
import { debounce as _debounce } from "lodash";
import nspell from "nspell";
import searchIcon from "@img/search-icon.png";
import arrowUpIcon from "@img/arrow-up-solid.png";
import arrowDownIcon from "@img/arrow-down-solid.png";
import repeatIcon from "@img/repeat.png";
import folderIcon from "@img/folder.png";
import filesIcon from "@img/files.png";
import usersIcon from "@img/users.png";
import folderIconActive from "@img/folder-blue.png";
import filesIconActive from "@img/files-blue.png";
import usersIconActive from "@img/users-blue.png";
import RecentSearchesSidebar from "./RecentSearchesSidebar.vue";

// Import dictionary files as raw text
import aff from "../dictionaries/index.aff?raw";
import dic from "../dictionaries/index.dic?raw";
const props = defineProps({
    show: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(["close", "view-all-results"]);

const router = useRouter();

// Search state
const SEARCH_COOKIE_KEY = "__Secure-Search";
let searchHistory = {};
const searchQuery = ref("");
const activeTab = ref("project");
const isSearching = ref(false);

// Spell check state
const showSpellTooltip = ref(false);
const spellSuggestion = ref("");
const misspelledWord = ref("");
const tooltipPosition = ref({ x: 0, y: 0 });
const searchInputRef = ref(null);
let spell;

const searchResults = ref({
    projects: [],
    documents: [],
    tamers: [],
    projects_count: 0,
    documents_count: 0,
    tamers_count: 0,
});

const recentSearches = ref([]);
const recentlyOpened = ref([]);
const showRecentSidebar = ref(false);
const selectedIndex = ref(-1);

const flattenedItems = computed(() => {
    if (!searchQuery.value || searchQuery.value.trim().length === 0) {
        const history = [];
        recentSearches.value
            .slice(0, 4)
            .forEach((item) =>
                history.push({ ...item, actualType: "keyword" }),
            );
        recentlyOpened.value
            .slice(0, 5)
            .forEach((item) =>
                history.push({ ...item, actualType: item.type }),
            );
        return history;
    }

    if (activeTab.value === "project") {
        return searchResults.value.projects.map((item) => ({
            ...item,
            actualType: "project",
        }));
    } else if (activeTab.value === "document") {
        return searchResults.value.documents.map((item) => ({
            ...item,
            actualType: "document",
        }));
    } else if (activeTab.value === "tamer") {
        return searchResults.value.tamers.map((item) => ({
            ...item,
            actualType: "tamer",
        }));
    }
    return [];
});

const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
        e.preventDefault();
        if (selectedIndex.value < flattenedItems.value.length - 1) {
            selectedIndex.value++;
            scrollToSelected();
        }
    } else if (e.key === "ArrowUp") {
        e.preventDefault();
        if (selectedIndex.value > 0) {
            selectedIndex.value--;
            scrollToSelected();
        }
    } else if (e.key === "Enter") {
        if (
            selectedIndex.value >= 0 &&
            selectedIndex.value < flattenedItems.value.length
        ) {
            e.preventDefault();
            const item = flattenedItems.value[selectedIndex.value];
            selectSearchItem(item, item.actualType);
        } else {
            handleSearch();
        }
    } else if (e.key === "Escape") {
        closeModal();
    }
};

const scrollToSelected = () => {
    nextTick(() => {
        const selectedEl = document.querySelector(".search-item.selected");
        if (selectedEl) {
            selectedEl.scrollIntoView({ block: "nearest", behavior: "smooth" });
        }
    });
};

watch([searchQuery, activeTab], () => {
    selectedIndex.value = -1;
});

const navigateDown = () => {
    if (selectedIndex.value < flattenedItems.value.length - 1) {
        selectedIndex.value++;
        scrollToSelected();
    }
};

const navigateUp = () => {
    if (selectedIndex.value > 0) {
        selectedIndex.value--;
        scrollToSelected();
    }
};

const selectCurrent = () => {
    if (
        selectedIndex.value >= 0 &&
        selectedIndex.value < flattenedItems.value.length
    ) {
        const item = flattenedItems.value[selectedIndex.value];
        selectSearchItem(item, item.actualType);
    } else {
        handleSearch();
    }
};

// Initialize spell checker
onMounted(() => {
    try {
        spell = nspell(aff, dic);
        console.log("Spell checker initialized successfully");
    } catch (error) {
        console.error("Failed to initialize spell checker:", error);
    }
});

// Search API call with debounce
const performSearch = _debounce((query) => {
    if (!query || query.trim().length === 0) {
        searchResults.value = {
            projects: [],
            documents: [],
            tamers: [],
            projects_count: 0,
            documents_count: 0,
            tamers_count: 0,
        };
        isSearching.value = false;
        return;
    }

    isSearching.value = true;
    SearchJobApi.search({ Keyword: query })
        .then((response) => {
            if (response.data && response.data.data) {
                searchResults.value = {
                    projects: response.data.data.projects || [],
                    documents: response.data.data.documents || [],
                    tamers:
                        response.data.data.tamers ||
                        response.data.data.users ||
                        [],
                    projects_count: response.data.data.projects_count || 0,
                    documents_count: response.data.data.documents_count || 0,
                    tamers_count:
                        response.data.data.tamers_count ||
                        response.data.data.users_count ||
                        0,
                };
            }
        })
        .catch((error) => {
            console.error("Search error:", error);
            searchResults.value = {
                projects: [],
                documents: [],
                tamers: [],
                projects_count: 0,
                documents_count: 0,
                tamers_count: 0,
            };
        })
        .finally(() => {
            isSearching.value = false;
        });
}, 500);

// Watch search query
watch(searchQuery, (newValue) => {
    if (newValue && newValue.trim().length > 0) {
        // When typing starts, set projects as active tab
        if (!isSearching.value && searchResults.value.projects.length === 0) {
            activeTab.value = "project";
        }
        performSearch(newValue);
    } else {
        searchResults.value = {
            projects: [],
            documents: [],
            tamers: [],
            projects_count: 0,
            documents_count: 0,
            tamers_count: 0,
        };
        isSearching.value = false;
    }
});

// Watch show prop to load history
watch(
    () => props.show,
    (newValue) => {
        if (newValue) {
            loadSearchHistory();
            // Focus the modal search input after modal opens
            setTimeout(() => {
                const modalInput =
                    document.getElementById("modal-search-input");
                if (modalInput) modalInput.focus();
            }, 100);
        }
    },
);

const loadSearchHistory = () => {
    // Always reload fresh search history from storage when opening
    searchHistory = Storage.get(SEARCH_COOKIE_KEY) || {
        keywords: [],
        items: [],
    };

    // Ensure arrays exist
    if (!searchHistory.keywords) searchHistory.keywords = [];
    if (!searchHistory.items) searchHistory.items = [];

    // Filter out empty or whitespace-only keywords
    const originalLength = searchHistory.keywords.length;
    searchHistory.keywords = searchHistory.keywords.filter(
        (keyword) => keyword && keyword.trim().length > 0,
    );

    // Save cleaned history back to storage if we removed any empty items
    if (originalLength !== searchHistory.keywords.length) {
        Storage.set(SEARCH_COOKIE_KEY, searchHistory);
    }

    if (searchHistory.keywords.length > 0) {
        recentSearches.value = searchHistory.keywords.map((keyword, idx) => ({
            id: `search-${Date.now()}-${idx}`,
            title: keyword,
            type: "keyword",
            icon: "search",
        }));
    } else {
        recentSearches.value = [];
    }

    if (searchHistory.items.length > 0) {
        recentlyOpened.value = searchHistory.items;
    } else {
        recentlyOpened.value = [];
    }
};

const closeModal = () => {
    searchQuery.value = "";
    emit("close");
};

const handleViewAllResults = (tab) => {
    handleSearch();
    return false;
    if (!searchQuery.value || searchQuery.value.trim().length === 0) return;

    // Store the search query before closing modal (which clears it)
    const searchKeyword = searchQuery.value;

    closeModal();
    
    // Emit event to open full search results sidebar
    emit("view-all-results", searchKeyword, tab);
};

const handleSearch = () => {
    if (!searchQuery.value || searchQuery.value.trim().length === 0) return;

    searchHistory = Storage.get(SEARCH_COOKIE_KEY) || {
        keywords: [],
        items: [],
    };
    if (!searchHistory.keywords) searchHistory.keywords = [];

    const existingIndex = searchHistory.keywords.indexOf(searchQuery.value);
    if (existingIndex > -1) {
        searchHistory.keywords.splice(existingIndex, 1);
    }

    // Add to beginning
    searchHistory.keywords.unshift(searchQuery.value);

    // Keep only last 5
    if (searchHistory.keywords.length > 5) {
        searchHistory.keywords = searchHistory.keywords.slice(0, 5);
    }

    Storage.set(SEARCH_COOKIE_KEY, searchHistory);

    const savedHistory = Storage.get(SEARCH_COOKIE_KEY);

    recentSearches.value = searchHistory.keywords.map((keyword, idx) => ({
        id: `search-${idx}`,
        title: keyword,
        type: "keyword",
        icon: "search",
    }));

    // Store the search query before closing modal (which clears it)
    const searchKeyword = searchQuery.value;
    const currentTab = activeTab.value || "project";

    closeModal();
    let currentRoute = router.currentRoute.value.name;
    console.log("Current route:", currentRoute);
    if (currentRoute === "search") {
        window.location.href = `/search?Keyword=${searchKeyword}&currentTab=${currentTab}`;
    } else {
        router.push({
            name: "search",
            query: { Keyword: searchKeyword, currentTab: currentTab },
        });
    }
};

const selectSearchItem = (item, type) => {
    // Save to recently opened if it's a project or document
    if (type === "project" || type === "document") {
        searchHistory = Storage.get(SEARCH_COOKIE_KEY) || {
            keywords: [],
            items: [],
        };
        if (!searchHistory.items) searchHistory.items = [];

        const existingIndex = searchHistory.items.findIndex(
            (i) => i.id === item.id && i.type === type,
        );
        if (existingIndex > -1) {
            searchHistory.items.splice(existingIndex, 1);
        }

        searchHistory.items.unshift({
            id: item.id,
            title: item.title || item.name,
            type: type,
            category: item.category,
            // 🔍 FIX: Store detailed metadata to match search results structure
            businessDomains: item.businessDomains || [],
            offerings: item.offerings || [],
            businessUnits: item.businessUnits || [],
            documentType: item.documentType || null,
            track: item.track || null,
            client: item.client || null,
        });

        if (searchHistory.items.length > 5) {
            searchHistory.items = searchHistory.items.slice(0, 5);
        }
        Storage.set(SEARCH_COOKIE_KEY, searchHistory);

        // Update recentlyOpened immediately
        recentlyOpened.value = searchHistory.items;
    }

    // Handle keyword selection (from recent searches)
    if (type === "keyword") {
        // Set the search query and trigger search
        searchQuery.value = item.title;
        // Don't close modal yet, let handleSearch do it
        handleSearch();
        return; // Exit early since handleSearch will handle navigation and closing
    }

    // Close modal for non-keyword selections
    closeModal();

    // Navigate based on type
    if (type === "project") {
        router.push({
            name: "projects.view",
            params: { id: item.id },
        });
    } else if (type === "document") {
        router.push({
            name: "documents.view",
            params: { id: item.id },
        });
    } else if (type === "tamer") {
        // Navigate to user profile or handle TAMers navigation
        console.log("Navigate to TAMer:", item);
    }
};

const clearRecentlyOpened = () => {
    searchHistory = Storage.get(SEARCH_COOKIE_KEY) || {
        keywords: [],
        items: [],
    };
    searchHistory.items = [];
    Storage.set(SEARCH_COOKIE_KEY, searchHistory);
    // Update reactive array immediately
    recentlyOpened.value = [];
};

const switchTab = (tab) => {
    activeTab.value = tab;
};

const getIconClass = (type) => {
    const classes = {
        document: "icon-purple",
        project: "",
        tamer: "icon-blue",
    };
    return classes[type] || "icon-purple";
};

const getIconType = (type) => {
    const icons = {
        document: ["far", "file-alt"],
        project: ["fas", "circle"],
        tamer: ["fas", "headphones"],
    };
    return icons[type] || ["far", "file-alt"];
};

// Spell check functions
function checkSpelling() {
    if (!spell || !searchQuery.value.trim()) {
        hideSpellTooltip();
        return;
    }

    const words = searchQuery.value.trim().split(/\s+/);

    // Find first misspelled word
    for (let i = 0; i < words.length; i++) {
        const word = words[i];
        const cleanWord = word.replace(/[^\w]/g, "");

        if (cleanWord.length > 2 && !spell.correct(cleanWord)) {
            const suggestions = spell.suggest(cleanWord);

            if (suggestions && suggestions.length > 0) {
                misspelledWord.value = word;
                spellSuggestion.value = suggestions[0]; // Use first suggestion

                // Position tooltip near the input
                if (searchInputRef.value) {
                    const rect = searchInputRef.value.getBoundingClientRect();
                    tooltipPosition.value = {
                        x: rect.left + i * 60, // Approximate word position
                        y: rect.bottom + 5,
                    };
                }

                showSpellTooltip.value = true;
                return;
            }
        }
    }

    // No misspellings found - show success message briefly
    misspelledWord.value = "";
    spellSuggestion.value = "✓ No spelling issues found";
    showSpellTooltip.value = true;

    setTimeout(() => {
        hideSpellTooltip();
    }, 2000);
}

function replaceWithSuggestion() {
    if (!misspelledWord.value || !spellSuggestion.value) return;

    // Replace the misspelled word with suggestion
    searchQuery.value = searchQuery.value.replace(
        misspelledWord.value,
        spellSuggestion.value,
    );

    hideSpellTooltip();

    // Focus back on input
    nextTick(() => {
        if (searchInputRef.value) {
            searchInputRef.value.focus();
        }
    });
}

function hideSpellTooltip() {
    showSpellTooltip.value = false;
    spellSuggestion.value = "";
    misspelledWord.value = "";
}

// Custom highlight function for search results
function highlightText(text, query) {
    if (!text) return "";
    if (!query || query.trim().length === 0) return text;

    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`(${escapedQuery})`, "gi");
    return text
        .toString()
        .replace(regex, '<span class="search-highlight">$1</span>');
}
</script>

<template>
    <!-- Search Modal -->
    <transition name="fade">
        <div v-if="show" class="search-modal-backdrop" @click="closeModal">
            <div class="search-modal" @click.stop>
                <!-- Modal Header with Search Input -->
                <div class="search-modal-header">
                    <div class="search-modal-icon">
                        <svg
                            width="20"
                            height="20"
                            viewBox="0 0 17 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                opacity="0.4"
                                d="M7.16569 4.1665C6.95647 4.1665 6.76937 4.29677 6.69676 4.49299L6.35287 5.42235C5.88605 6.68391 5.69634 7.16579 5.34732 7.5148C4.99831 7.86382 4.51643 8.05353 3.25487 8.52035L2.32551 8.86424C2.12929 8.93685 1.99902 9.12395 1.99902 9.33317C1.99902 9.54239 2.12929 9.72949 2.32551 9.8021L3.25488 10.146C4.51643 10.6128 4.99831 10.8025 5.34732 11.1515C5.69634 11.5006 5.88605 11.9824 6.35287 13.244L6.69676 14.1734C6.76937 14.3696 6.95647 14.4998 7.16569 14.4998C7.37491 14.4998 7.56201 14.3696 7.63462 14.1734L7.97851 13.244C8.44533 11.9824 8.63504 11.5006 8.98406 11.1515C9.33307 10.8025 9.81495 10.6128 11.0765 10.146L12.0059 9.80209C12.2021 9.72949 12.3324 9.54239 12.3324 9.33317C12.3324 9.12395 12.2021 8.93685 12.0059 8.86424L11.0765 8.52035C9.81495 8.05353 9.33307 7.86382 8.98406 7.5148C8.63504 7.16579 8.44533 6.68391 7.97851 5.42235L7.63462 4.49299C7.56201 4.29677 7.37491 4.1665 7.16569 4.1665Z"
                                fill="#412CC9"
                            />
                            <path
                                d="M12.499 6.5C12.7081 6.5 12.8951 6.36986 12.9678 6.17383L13.1152 5.77539C13.3244 5.21021 13.3855 5.07646 13.4805 4.98145C13.5755 4.88643 13.7092 4.82535 14.2744 4.61621L14.6729 4.46875C14.8689 4.39606 14.999 4.2091 14.999 4C14.999 3.7909 14.8689 3.60394 14.6729 3.53125L14.2744 3.38379C13.7092 3.17465 13.5755 3.11357 13.4805 3.01855C13.3855 2.92354 13.3244 2.78979 13.1152 2.22461L12.9678 1.82617C12.8951 1.63014 12.7081 1.5 12.499 1.5C12.2899 1.5 12.103 1.63014 12.0303 1.82617L11.8828 2.22461C11.6737 2.78979 11.6126 2.92354 11.5176 3.01855C11.4226 3.11357 11.2888 3.17465 10.7236 3.38379L10.3252 3.53125C10.1292 3.60394 9.99902 3.7909 9.99902 4C9.99902 4.2091 10.1292 4.39605 10.3252 4.46875L10.7236 4.61621C11.2888 4.82535 11.4226 4.88643 11.5176 4.98145C11.6126 5.07646 11.6737 5.21021 11.8828 5.77539L12.0303 6.17383C12.103 6.36986 12.2899 6.5 12.499 6.5Z"
                                fill="#412CC9"
                            />
                        </svg>
                    </div>
                    <div style="position: relative; flex: 1">
                        <input
                            id="modal-search-input"
                            ref="searchInputRef"
                            v-model="searchQuery"
                            class="search-modal-input"
                            placeholder="Ask Anything in Osharek Knowledge Base (Projects, Docs, TAMer(s)…"
                            role="combobox"
                            aria-autocomplete="list"
                            aria-expanded="true"
                            aria-haspopup="listbox"
                            aria-controls="search-results-list"
                            :aria-activedescendant="
                                selectedIndex >= 0
                                    ? `option-${selectedIndex}`
                                    : undefined
                            "
                            @keyup.enter="handleSearch"
                            @keydown="handleKeyDown"
                            @keyup.esc="closeModal"
                        />

                        <!-- Spell Check Button -->
                        <button
                            v-if="searchQuery.trim().length > 0"
                            @click="checkSpelling"
                            class="spell-check-btn"
                            title="Check spelling"
                        >
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M14.5 2c1.5 0 3 .5 3 2.6L16 14l-1 4-3-1.5L9 18l-1-4L6.5 4.6C6.5 2.5 8 2 9.5 2zM9 16l3-1 3 1-1-4H10l-1 4z"
                                    fill="currentColor"
                                />
                                <path
                                    d="M3 20h18v2H3v-2z"
                                    fill="currentColor"
                                />
                            </svg>
                        </button>

                        <!-- Spell Check Tooltip -->
                        <div v-if="showSpellTooltip" class="spell-tooltip">
                            <div
                                v-if="misspelledWord"
                                @click="replaceWithSuggestion"
                                class="spell-suggestion"
                            >
                                <span class="misspelled">{{
                                    misspelledWord
                                }}</span>
                                → <strong>{{ spellSuggestion }}</strong>
                                <small>(click to fix)</small>
                            </div>
                            <div v-else class="spell-success">
                                {{ spellSuggestion }}
                            </div>
                            <button
                                @click="hideSpellTooltip"
                                class="tooltip-close"
                            >
                                ×
                            </button>
                        </div>
                    </div>
                    <button class="search-modal-close" @click="handleSearch">
                        <img :src="searchIcon" alt="Close" />
                    </button>
                </div>

                <!-- Modal Content -->
                <div class="search-modal-content">
                    <!-- Show Recent Searches when input is empty -->
                    <template
                        v-if="!searchQuery || searchQuery.trim().length === 0"
                    >
                        <div
                            v-if="recentSearches.length > 0"
                            class="search-section recent-section"
                        >
                            <div class="search-section-header">
                                <span class="section-title">Recent Search</span>
                                <span
                                    class="view-more"
                                    @click="showRecentSidebar = true"
                                    >View More</span
                                >
                            </div>
                            <div class="search-items">
                                <div
                                    v-for="(
                                        item, index
                                    ) in recentSearches.slice(0, 4)"
                                    :key="item.id"
                                    class="search-item recent-item"
                                    :class="{
                                        selected: selectedIndex === index,
                                    }"
                                    role="option"
                                    :id="`option-${index}`"
                                    :aria-selected="selectedIndex === index"
                                    @click="selectSearchItem(item, 'keyword')"
                                >
                                    <div class="recent-icon">
                                        <font-awesome-icon
                                            :icon="['far', 'clock']"
                                        />
                                    </div>
                                    <span class="search-title">{{
                                        item.title
                                    }}</span>
                                    <div class="arrow-icon">
                                        <img
                                            src="/img/arrow-up-right.svg"
                                            alt="Arrow Up Right"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div
                            v-if="recentlyOpened.length > 0"
                            class="search-section recently-opened-section"
                        >
                            <div class="search-section-header">
                                <span class="section-title"
                                    >Recently Opened</span
                                >
                                <span
                                    class="clear-link"
                                    @click="clearRecentlyOpened"
                                    >Clear</span
                                >
                            </div>
                            <div class="search-items">
                                <div
                                    v-for="(
                                        item, index
                                    ) in recentlyOpened.slice(0, 5)"
                                    :key="item.id"
                                    class="search-item opened-item"
                                    :class="{
                                        selected:
                                            selectedIndex ===
                                            index +
                                                Math.min(
                                                    recentSearches.length,
                                                    4,
                                                ),
                                    }"
                                    role="option"
                                    :id="`option-${index + Math.min(recentSearches.length, 4)}`"
                                    :aria-selected="
                                        selectedIndex ===
                                        index +
                                            Math.min(recentSearches.length, 4)
                                    "
                                    @click="selectSearchItem(item, item.type)"
                                >
                                    <div
                                        class="opened-icon"
                                        :class="getIconClass(item.type)"
                                    >
                                        <img
                                            v-if="item.type === 'document'"
                                            :src="
                                                Storage.getTypeIcon(
                                                    item.category,
                                                )
                                            "
                                            class="avatar-img"
                                            alt="Doc"
                                            style="border-radius: 50%"
                                        />
                                        <img
                                            v-else-if="item.type === 'project' && item.client?.avatar?.url"
                                            :src="item.client.avatar.url"
                                            class="avatar-img"
                                            alt="Project"
                                        />
                                        <img
                                            v-else
                                            src="/img/project-icon.png"
                                            class="avatar-img"
                                            alt="Project"
                                        />
                                    </div>
                                    <div class="opened-content">
                                        <div class="opened-title">
                                            {{ item.title }}
                                        </div>
                                        <div class="opened-meta">
                                            <span v-if="item.metadata">{{
                                                item.metadata
                                            }}</span>
                                            <span
                                                v-else-if="
                                                    item.type === 'document'
                                                "
                                            >
                                                {{ item.documentType?.name || "---" }} |
                                                {{ item.offerings?.map(offer => offer.name).join(', ') || '' }} |
                                                {{ item.track?.name || "Not applicable" }}
                                            </span>
                                            <span
                                                v-else-if="
                                                    item.type === 'project'
                                                "
                                            >
                                                {{ item.businessDomains?.map(domain => domain.name).join(', ') || '' }} |
                                                {{ item.offerings?.map(offer => offer.name).join(', ') || '' }} |
                                                {{ item.businessUnits?.map(unit => unit.name).join(', ') || '' }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Empty state for no history -->
                        <div
                            v-if="
                                recentSearches.length === 0 &&
                                recentlyOpened.length === 0
                            "
                            class="search-empty-state"
                        >
                            <font-awesome-icon
                                :icon="['fas', 'search']"
                                style="
                                    font-size: 32px;
                                    color: #ccc;
                                    margin-bottom: 12px;
                                "
                            />
                            <p>Start typing to search...</p>
                        </div>
                    </template>

                    <!-- Show Search Results with Tabs when typing -->
                    <template v-else>
                        <!-- Tabs -->
                        <div class="search-tabs">
                            <div
                                class="search-tab"
                                :class="{ active: activeTab === 'project' }"
                                @click="switchTab('project')"
                            >
                                <img
                                    v-if="activeTab === 'project'"
                                    :src="folderIconActive"
                                    alt="Projects"
                                    width="16"
                                    height="16"
                                />
                                <img
                                    v-else
                                    :src="folderIcon"
                                    alt="Projects"
                                    width="16"
                                    height="16"
                                />
                                <span
                                    class="tab-text"
                                    :style="{
                                        color:
                                            activeTab === 'project'
                                                ? '#3625A7'
                                                : '#46454D',
                                    }"
                                    >Projects</span
                                >
                            </div>
                            <div
                                class="search-tab"
                                :class="{ active: activeTab === 'document' }"
                                @click="switchTab('document')"
                            >
                                <img
                                    v-if="activeTab === 'document'"
                                    :src="filesIconActive"
                                    alt="Documents"
                                    width="16"
                                    height="16"
                                />
                                <img
                                    v-else
                                    :src="filesIcon"
                                    alt="Documents"
                                    width="16"
                                    height="16"
                                />
                                <span
                                    class="tab-text"
                                    :style="{
                                        color:
                                            activeTab === 'document'
                                                ? '#3625A7'
                                                : '#46454D',
                                    }"
                                    >Documents</span
                                >
                            </div>
                            <div
                                class="search-tab"
                                :class="{ active: activeTab === 'tamer' }"
                                @click="switchTab('tamer')"
                            >
                                <img
                                    v-if="activeTab === 'tamer'"
                                    :src="usersIconActive"
                                    alt="Users"
                                    width="16"
                                    height="16"
                                />
                                <img
                                    v-else
                                    :src="usersIcon"
                                    alt="Users"
                                    width="16"
                                    height="16"
                                />
                                <span
                                    class="tab-text"
                                    :style="{
                                        color:
                                            activeTab === 'tamer'
                                                ? '#3625A7'
                                                : '#46454D',
                                    }"
                                    >Users</span
                                >
                            </div>
                        </div>

                        <!-- Results Content -->
                        <div class="search-results-content">
                            <!-- Loading State -->
                            <div v-if="isSearching" class="search-loading">
                                <font-awesome-icon
                                    :icon="['fas', 'spinner']"
                                    spin
                                />
                                <span>Searching...</span>
                            </div>

                            <!-- Projects Tab -->
                            <div v-else-if="activeTab === 'project'">
                                <div
                                    v-if="searchResults.projects.length > 0"
                                    class="search-section-header results-header"
                                >
                                    <span class="section-title">Projects</span>
                                    <span
                                        class="view-all-link"
                                        @click="handleViewAllResults('project')"
                                        >View All ({{
                                            searchResults.projects_count
                                        }})</span
                                    >
                                </div>
                                <div class="search-items">
                                    <div
                                        v-for="(
                                            item, index
                                        ) in searchResults.projects"
                                        :key="item.id"
                                        class="search-item"
                                        :class="{
                                            selected: selectedIndex === index,
                                        }"
                                        role="option"
                                        :id="`option-${index}`"
                                        :aria-selected="selectedIndex === index"
                                        @click="
                                            selectSearchItem(item, 'project')
                                        "
                                    >
                                        <div class="search-icon project-icon">
                                            <img
                                                v-if="item.client?.avatar?.url"
                                                :src="item.client.avatar.url"
                                                class="avatar-img"
                                            />
                                            <font-awesome-icon
                                                v-else
                                                :icon="['fas', 'folder']"
                                            />
                                        </div>
                                        <div class="search-item-content">
                                            <span
                                                class="search-title"
                                                :title="
                                                    item.title || item.project_name
                                                "
                                                v-html="highlightText(item.title || item.project_name, searchQuery)"
                                            ></span>
                                            <span
                                                class="search-subtitle"
                                            >
                                                {{ item.businessDomains?.map(domain => domain.name).join(', ') || '' }} |
                                                {{ item.offerings?.map(offer => offer.name).join(', ') || '' }} |
                                                {{ item.businessUnits?.map(unit => unit.name).join(', ') || '' }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div
                                    v-if="searchResults.projects.length === 0"
                                    class="search-empty"
                                >
                                    <img src="/img/no-project.svg" alt="" />
                                    <p class="empty-title">No projects found</p>
                                    <p class="empty-subtitle">
                                        “Please, Check Your Context or
                                        Keywords."
                                    </p>
                                </div>
                            </div>

                            <!-- Documents Tab -->
                            <div v-else-if="activeTab === 'document'">
                                <div
                                    v-if="searchResults.documents.length > 0"
                                    class="search-section-header results-header"
                                >
                                    <span class="section-title">Documents</span>
                                    <span
                                        class="view-all-link"
                                        @click="handleViewAllResults('document')"
                                        >View All ({{
                                            searchResults.documents_count
                                        }})</span
                                    >
                                </div>
                                <div class="search-items">
                                    <div
                                        v-for="(
                                            item, index
                                        ) in searchResults.documents"
                                        :key="item.id"
                                        class="search-item"
                                        :class="{
                                            selected: selectedIndex === index,
                                        }"
                                        role="option"
                                        :id="`option-${index}`"
                                        :aria-selected="selectedIndex === index"
                                        @click="
                                            selectSearchItem(item, 'document')
                                        "
                                    >
                                        <div class="search-icon">
                                            <img
                                                :src="
                                                    Storage.getTypeIcon(
                                                        item.category,
                                                    )
                                                "
                                                class="avatar-img"
                                                alt="Doc"
                                            />
                                        </div>
                                        <div class="search-item-content">
                                            <span class="search-title" v-html="highlightText(item.title || item.name, searchQuery)"></span>
                                            <span class="search-subtitle">
                                                {{ item.documentType?.name || "---" }} |
                                                {{ item.offerings?.map(offer => offer.name).join(', ') || '' }} |
                                                {{ item.track?.name || "Not applicable" }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div
                                    v-if="searchResults.documents.length === 0"
                                    class="search-empty"
                                >
                                    <img src="/img/no-document.svg" alt="" />
                                    <p class="empty-title">
                                        No documents found
                                    </p>
                                    <p class="empty-subtitle">
                                        “Please, Check Your Context or
                                        Keywords."
                                    </p>
                                </div>
                            </div>

                            <!-- TAMers Tab -->
                            <div v-else-if="activeTab === 'tamer'">
                                <div
                                    v-if="searchResults.tamers.length > 0"
                                    class="search-section-header results-header"
                                >
                                    <span class="section-title">Users</span>
                                    <span
                                        class="view-all-link"
                                        @click="handleViewAllResults('tamer')"
                                        >View All ({{
                                            searchResults.tamers_count
                                        }})</span
                                    >
                                </div>
                                <div class="search-items">
                                    <div
                                        v-for="(
                                            item, index
                                        ) in searchResults.tamers"
                                        :key="item.id"
                                        class="search-item"
                                        :class="{
                                            selected: selectedIndex === index,
                                        }"
                                        role="option"
                                        :id="`option-${index}`"
                                        :aria-selected="selectedIndex === index"
                                        @click="selectSearchItem(item, 'tamer')"
                                    >
                                        <div class="search-icon user-icon">
                                            <img
                                                v-if="
                                                    item.avatar?.url ||
                                                    item.avatar
                                                "
                                                :src="
                                                    item.avatar?.url ||
                                                    item.avatar
                                                "
                                                class="avatar-img"
                                            />
                                            <font-awesome-icon
                                                v-else
                                                :icon="['fas', 'user']"
                                            />
                                        </div>
                                        <div class="search-item-content">
                                            <span class="search-title" v-html="highlightText(item.name || item.username, searchQuery)"></span>
                                            <span
                                                class="search-subtitle"
                                            >
                                                {{ item.email || 'No email' }} |
                                                {{ item.role_name || item.role || 'Member' }} |
                                                {{ item.department || 'Osharek Team' }}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div
                                    v-if="searchResults.tamers.length === 0"
                                    class="search-empty"
                                >
                                    <img src="/img/no-users.svg" alt="" />
                                    <p class="empty-title">No User(s) Found</p>
                                    <p class="empty-subtitle">
                                        “Please, Check Your Context or
                                        Keywords."
                                    </p>
                                </div>
                            </div>
                        </div>
                    </template>
                </div>

                <!-- Footer with keyboard shortcuts -->
                <div class="search-modal-footer">
                    <div class="footer-shortcuts">
                        <div
                            class="shortcut-item"
                            @click="selectCurrent"
                            style="cursor: pointer"
                        >
                            <kbd class="kbd-key enter-key">
                                <img :src="repeatIcon" alt="Repeat" />
                            </kbd>
                            <span>To Select</span>
                        </div>
                        <div class="shortcut-item">
                            <kbd
                                class="kbd-key"
                                @click="navigateDown"
                                style="cursor: pointer"
                            >
                                <img
                                    src="/img/arrow-bottom.svg"
                                    alt="Arrow Down"
                                />
                            </kbd>
                            <kbd
                                class="kbd-key"
                                @click="navigateUp"
                                style="cursor: pointer"
                            >
                                <img src="/img/arrow-top.svg" alt="Arrow Up" />
                            </kbd>
                            <span>To Navigate</span>
                        </div>
                        <div
                            class="shortcut-item"
                            @click="closeModal"
                            style="cursor: pointer"
                        >
                            <kbd class="kbd-key">
                                <img style="width: 20px;" src="/img/esc.svg" alt="Esc" />
                            </kbd>
                            <span>To close</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </transition>

    <!-- Recent Searches Sidebar -->
    <RecentSearchesSidebar
        :show="showRecentSidebar"
        @close="showRecentSidebar = false"
    />
</template>

<style scoped>
/* Search Modal Backdrop */
.search-modal-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 99999;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 60px 20px 20px;
    overflow-y: auto;
}

/* Search Modal */
.search-modal {
    background: white;
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
    width: 100%;
    max-width: 700px;
    height: 1080px;
    max-height: calc(100vh - 100px);
    display: flex;
    justify-content: space-between;
    flex-direction: column;
    animation: slideDown 0.2s ease-out;
}

.tab-text {
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    color: #46454d;
}

@keyframes slideDown {
    from {
        opacity: 0;
        transform: translateY(-20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

/* Modal Header */
.search-modal-header {
    display: flex;
    align-items: center;
    padding: 20px 24px;
    border-bottom: 1px solid #e8e8e8;
    gap: 12px;
}

.search-modal-icon {
    flex-shrink: 0;
}

.search-modal-input {
    flex: 1;
    width: 100%;
    border: none;
    outline: none;
    font-size: 16px;
    color: #1a1a1a;
    font-family: inherit;
    padding-right: 48px;
    box-sizing: border-box;
}

.search-modal-input::placeholder {
    color: #999;
}

.search-modal-close {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    border-radius: 5px;
    border: 1px solid #dfdce5;
    cursor: pointer;
    background: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
}

.search-modal-close:hover {
    background: #e8e8e8;
    color: #333;
}

/* Modal Content */
.search-modal-content {
    flex: 1;
    overflow-y: auto;
    max-height: 900px;
}

/* Modal Footer */
.search-modal-footer {
    border-top: 1px solid #e8e8e8;
    background: white;
    padding: 12px 20px;
    border-radius: 0 0 12px 12px;
    background-color: #f9fafb;
}

/* Fade Transition */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

/* Empty State */
.search-empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    color: #999;
    text-align: center;
}

.search-empty-state p {
    margin: 0;
    font-size: 14px;
}

/* Tabs */
.search-tabs {
    display: flex;
    padding: 16px 16px 12px;
    gap: 8px;
}

.search-tab {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    cursor: pointer;
    border-radius: 20px;
    /* border: 1px solid #d0d0d0; */
    background: white;
    transition: all 0.2s;
    font-size: 13px;
    color: #666;
    font-weight: 400;
}

.search-tab i {
    font-size: 14px;
    color: #666;
}

.search-tab:hover {
    border-color: #b0b0b0;
    background: #f8f8f8;
}

.search-tab.active {
    background: #f5f3ff;
    color: #412cc9;
    border-color: #412cc9;
}

.search-tab.active i {
    color: #412cc9;
}

.tab-count {
    background: #e0e0e0;
    color: #666;
    font-size: 11px;
    padding: 2px 7px;
    border-radius: 10px;
    margin-left: 2px;
    font-weight: 500;
}

.search-tab.active .tab-count {
    background: #412cc9;
    color: white;
}

/* Search Results Content */
.search-results-content {
    flex: 1;
    overflow-y: auto;
    max-height: 800px;
}

/* Loading State */
.search-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px 20px;
    gap: 12px;
    color: #666;
}

.search-loading i {
    font-size: 24px;
    color: #412cc9;
}

/* Search Section */
.search-section {
    padding: 12px 0;
}

.search-section:not(:last-child) {
    border-bottom: 1px solid #e8e8e8;
}

.search-section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 20px;
    margin-bottom: 8px;
}

.search-section-header .section-title {
    font-size: 14px;
    font-weight: 500;
    color: #1a1a1a;
}

.view-more {
    font-size: 13px;
    color: #5e45ff;
    cursor: pointer;
    transition: opacity 0.2s;
    font-weight: 400;
}

.view-more:hover {
    opacity: 0.8;
    text-decoration: underline;
}

.clear-link {
    font-size: 13px;
    color: #5e45ff;
    cursor: pointer;
    transition: opacity 0.2s;
    font-weight: 400;
}

.clear-link:hover {
    opacity: 0.8;
    text-decoration: underline;
}

/* Recent Search Items */
.recent-item {
    display: flex;
    align-items: center;
    padding: 10px 20px;
    cursor: pointer;
    transition: background-color 0.2s;
    gap: 12px;
}

.recent-item:hover {
    background-color: #f8f8f8;
}

.recent-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    color: #666;
    font-size: 16px;
    flex-shrink: 0;
}

.recent-item .search-title {
    flex: 1;
    font-size: 14px;
    color: #333;
    font-weight: 400;
}

.arrow-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #5e45ff;
    font-size: 14px;
}

/* Recently Opened Items */
.opened-item {
    display: flex;
    align-items: center;
    padding: 12px 20px;
    cursor: pointer;
    transition: background-color 0.2s;
    gap: 12px;
}

.opened-item:hover {
    background-color: #f8f8f8;
}

.opened-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    font-size: 18px;
    flex-shrink: 0;
}

.opened-icon.icon-purple {
    color: #5e45ff;
}

.opened-icon.icon-dark {
    background-color: #2d2d2d;
    color: white;
}

.opened-icon.icon-blue {
    background-color: #d4e6ff;
    color: #2196f3;
}

.opened-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
}

.opened-title {
    font-size: 14px;
    color: #1a1a1a;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.opened-meta {
    font-size: 12px;
    color: #999;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* Search Items */
.search-items {
    display: flex;
    flex-direction: column;
}

.search-item {
    display: flex;
    align-items: center;
    padding: 10px 16px;
    cursor: pointer;
    transition: background-color 0.2s;
    gap: 12px;
}

.search-item:hover,
.search-item.selected {
    background-color: #f8f8ff;
    border-left: 3px solid #5e45ff;
}

.search-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    background-color: #e9e7fa;
    border-radius: 8px;
    color: #5e45ff;
    font-size: 16px;
    flex-shrink: 0;
    overflow: hidden;
}

.search-icon.project-icon,
.search-icon.user-icon {
    background-color: #e9e7fa;
}

.avatar-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
}

.search-item-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
}

.search-title {
    font-size: 14px;
    color: #161616;
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.search-subtitle {
    font-size: 12px;
    color: #999;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.search-type {
    font-size: 11px;
    color: #999;
    background-color: #f5f5f5;
    padding: 3px 8px;
    border-radius: 4px;
    text-transform: capitalize;
}

/* Empty results */
.search-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 40px 20px;
    color: #999;
}

.search-empty i {
    font-size: 32px;
    opacity: 0.5;
}

/* Footer */
.footer-shortcuts {
    display: flex;
    flex-wrap: wrap;
    gap: 24px;
    align-items: center;
    justify-content: center;
}

.shortcut-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #999;
}

.shortcut-item {
    color: #72707A;
    font-size: 14px;
    font-weight: 400;
}

.kbd-key {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 32px;
    height: 32px;
    padding: 0 8px;
    background: white;
    border: 1px solid #d0d0d0;
    border-radius: 5px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
    font-size: 11px;
    font-weight: 500;
    color: #666;
    font-family: inherit;
}

.kbd-key i {
    font-size: 12px;
}

.kbd-key.enter-key {
    min-width: 36px;
}

.kbd-key.esc-key {
    text-transform: lowercase;
    font-size: 10px;
    letter-spacing: 0.5px;
}

/* Scrollbar styling */
.search-results-content::-webkit-scrollbar,
.search-modal-content::-webkit-scrollbar {
    width: 6px;
}

.search-results-content::-webkit-scrollbar-track,
.search-modal-content::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
}

.search-results-content::-webkit-scrollbar-thumb,
.search-modal-content::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 10px;
}

.search-results-content::-webkit-scrollbar-thumb:hover,
.search-modal-content::-webkit-scrollbar-thumb:hover {
    background: #999;
}

/* Spell Check Styles */
.spell-check-btn {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    background: #f8f9fa;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    padding: 6px;
    color: #666;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    width: 28px;
    height: 28px;
}

.spell-check-btn:hover {
    background: #412cc9;
    color: white;
    border-color: #412cc9;
}

.spell-tooltip {
    position: absolute;
    top: calc(100% + 8px);
    left: 0;
    right: 0;
    background: white;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 1000;
    animation: fadeIn 0.2s ease-out;
}

.spell-suggestion {
    cursor: pointer;
    padding: 8px;
    border-radius: 6px;
    transition: background-color 0.2s;
    font-size: 14px;
    display: flex;
    align-items: center;
    gap: 8px;
}

.spell-suggestion:hover {
    background: #f8f9fa;
}

.misspelled {
    color: #dc3545;
    text-decoration: line-through;
    font-weight: 500;
}

.spell-suggestion strong {
    color: #28a745;
}

.spell-suggestion small {
    color: #6c757d;
    font-size: 12px;
}

.spell-success {
    color: #28a745;
    font-size: 14px;
    text-align: center;
    padding: 4px;
}

.tooltip-close {
    position: absolute;
    top: 8px;
    right: 8px;
    background: none;
    border: none;
    font-size: 16px;
    color: #999;
    cursor: pointer;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
}

.tooltip-close:hover {
    background: #f0f0f0;
    color: #666;
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(-5px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}
.empty-title {
    font-size: 16px;
    font-weight: 600;
    color: #3625a7;
    margin-bottom: 0px;
}
.empty-subtitle {
    font-size: 14px;
    font-weight: 400;
    color: #46454d;
    margin-top: 0px;
}
</style>
